<template>
  <div class="gallery-pieces app-page-wrapper flex flex-col absolute">
    <premium-menu />
    <section class="w-full h-full safe-area">
      <main class="gallery-pieces-main relative h-full flex">
        <side-menu />

        <section class="gallery-pieces-main-wrapper">
          <header-accessories
            :text-left="$t('premiumGroup.galleryPacks')"
            :text-right="$t('premiumGroup.yourGalleryPacks')"
            :multiple-items="ownedPacks"
          />

          <div v-if="!isContentLoading" class="wrapper relative flex">
            <section ref="animation1" class="flex w-full">
              <div
                v-for="(pack, index) in packs"
                :key="index"
                class="gallery-pack-premium-item animated-element--faded flex-shrink-0 mr-8"
              >
                <gallery-pack-premium :pack="pack" />
              </div>
            </section>
          </div>

          <component-loading height="39.75rem" :is-loading="isContentLoading" />
        </section>
      </main>
    </section>
  </div>
</template>

<script lang="ts">
import HeaderAccessories from '@/components/Premium/Accessories/HeaderAccessories.vue'
import SideMenu from '@/components/Premium/Accessories/SideMenu.vue'
import PremiumMenu from '@/components/Premium/PremiumMenu.vue'
import { animateElement } from '@/helpers'
import { mapState, mapActions } from 'pinia'
import { defineComponent } from 'vue'
import GalleryPackPremium from '@/components/Premium/Accessories/GalleryPackPremium.vue'
import { useGalleryPacksStore } from '@/store/pinia/gallery/packsStore'
import type { GalleryPack } from '@/interfaces/gallery/Packs'

interface OwnedPack {
  name: string
  value: number
}

export default defineComponent({
  name: 'PremiumGalleryPieces',

  components: {
    SideMenu,
    PremiumMenu,
    HeaderAccessories,
    GalleryPackPremium,
  },

  computed: {
    ...mapState(useGalleryPacksStore, ['packs']),

    isContentLoading(): boolean {
      return this.packs === null
    },

    ownedPacks(): OwnedPack[] {
      return this.packs?.map((pack: GalleryPack): OwnedPack => {
        return {
          name: (pack.pack_name as string).replaceAll('_', '-'),
          value: pack.amount
        }
      }) ?? []
    }
  },

  watch: {
    isContentLoading(loading: boolean): void {
      if (!loading) {
        this.$nextTick((): void => {
          this.animateScene()
        })
      }
    },
  },

  created(): void {
    this.loadPacks()
  },

  methods: {
    ...mapActions(useGalleryPacksStore, ['loadPacks']),

    animateScene(): void {
      const animatedBigElements = this.$refs.animation1.querySelectorAll(
        '.gallery-pack-premium-item',
      )
      animatedBigElements.forEach((element: HTMLElement, index: number): void => {
        const delay = 67 + index * 100
        animateElement(
          element,
          'fade-in, rotate-in',
          '0.87s',
          delay,
          'cubic-bezier(0.22, 1, 0.36, 1)',
        )
      })
    },
  },
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/animations.scss';

.gallery-pieces-main {
  &-wrapper {
    margin: 1.125rem 2.1875rem 0;
    flex-grow: 1;
    overflow: hidden;

    .wrapper {
      margin-top: 1.125rem;
      height: 39.75rem;
    }
  }
}
</style>
